import React, { useEffect, useRef, useCallback, useState } from "react";

/**
 * Global event bus for cart-add animation.
 * Call cartAnimBus.fire() from any cart-add success path.
 * Optionally register the cart icon's DOM ref so animation targets it precisely.
 */
export const cartAnimBus = {
  _listeners: [] as (() => void)[],
  _cartIconRef: null as HTMLElement | null,

  fire() {
    this._listeners.forEach((fn) => fn());
  },
  subscribe(fn: () => void) {
    this._listeners.push(fn);
    return () => {
      this._listeners = this._listeners.filter((f) => f !== fn);
    };
  },
  registerCartIcon(el: HTMLElement | null) {
    this._cartIconRef = el;
  },
  getCartIconPos(): { x: number; y: number } | null {
    if (!this._cartIconRef) return null;
    const rect = this._cartIconRef.getBoundingClientRect();
    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  },
};

/**
 * CartAddAnimation — mounts once at App level.
 * Listens for cartAnimBus events and runs a 1200ms animation:
 *   0–500ms:  ticket icon flies from screen center toward cart icon
 *   500–1200ms: cart area glows yellow
 *
 * Uses position:fixed + pointer-events:none — never affects layout (no CLS).
 * Animation is concurrent with backend POST — does not block UI.
 */
const CartAddAnimation: React.FC = () => {
  const [phase, setPhase] = useState<"idle" | "fly" | "glow">("idle");
  const [cartPos, setCartPos] = useState<{ x: number; y: number } | null>(null);
  const timer1 = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const timer2 = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const triggerAnim = useCallback(() => {
    const pos = cartAnimBus.getCartIconPos();
    setCartPos(pos);
    clearTimeout(timer1.current);
    clearTimeout(timer2.current);
    setPhase("fly");
    timer1.current = setTimeout(() => setPhase("glow"), 500);
    timer2.current = setTimeout(() => setPhase("idle"), 1200);
  }, []);

  useEffect(() => {
    const unsub = cartAnimBus.subscribe(triggerAnim);
    return () => {
      unsub();
      clearTimeout(timer1.current);
      clearTimeout(timer2.current);
    };
  }, [triggerAnim]);

  if (phase === "idle") return null;

  // Calculate translate so ticket flies toward the cart icon
  const targetX = cartPos ? cartPos.x - window.innerWidth / 2 : 120;
  const targetY = cartPos ? cartPos.y - window.innerHeight / 2 : -200;

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        pointerEvents: "none",
        zIndex: 9999,
      }}
    >
      {phase === "fly" && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            fontSize: 20,
            animation: "cartFly 500ms cubic-bezier(0.4, 0, 0.2, 1) forwards",
            // CSS custom properties for the target position
            ["--tx" as any]: `${targetX}px`,
            ["--ty" as any]: `${targetY}px`,
          }}
        >
          🎟️
        </div>
      )}
      {phase === "glow" && cartPos && (
        <div
          style={{
            position: "absolute",
            left: cartPos.x - 18,
            top: cartPos.y - 18,
            width: 36,
            height: 36,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(255,217,0,0.8) 0%, transparent 70%)",
            animation: "cartGlow 700ms ease-out forwards",
            pointerEvents: "none",
          }}
        />
      )}
      <style>{`
        @keyframes cartFly {
          0%   { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          80%  { opacity: 0.7; }
          100% { transform: translate(calc(-50% + var(--tx)), calc(-50% + var(--ty))) scale(0.2); opacity: 0; }
        }
        @keyframes cartGlow {
          0%   { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(3); }
        }
      `}</style>
    </div>
  );
};

export default CartAddAnimation;
